import {
    BankingGeneralDisclosure,
    CashApyDisclosure,
    CashBackDisclosure,
    InvestmentManagementDisclosure,
} from '@fruitful/compliance';
import { currentApy } from '@fruitful/fruitils';
import type {
    HolisticSummary,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    type NavigationState,
    type Route,
    type SceneRendererProps,
    TabBar,
    TabView,
} from 'react-native-tab-view';
import {
    Accordion,
    BodyMediumRegular,
    Button,
    ButtonVariant,
    CircleAlertIcon,
    FootnoteRegular,
    FruitfulLogoIcon,
    H1Emphasized,
    H2Emphasized,
    H5Emphasized,
    HeroEmphasized,
    Icon,
    InfoButtonWithTooltip,
    isNonMobileWeb,
    isWeb,
    LabelRegular,
    redactPrivate,
    Separator,
    Slider,
    TallSheet,
    TooltipGroupProvider,
    useThemeColors,
    XStack,
    YStack,
} from 'smoothie';
import { CountUp } from 'use-count-up';
import { useDebouncedCallback } from 'use-debounce';

import {
    type SliderType,
    useCountCalculatorInteractions,
} from '../hooks/useCountCalculatorInteractions';
import { useMoneyMapEarningsInputs } from '../hooks/useMoneyMapEarningsInputs';
import { useOnboardingEarningsInputs } from '../hooks/useOnboardingEarningsInputs';
import { type EventData } from '../types/eventTracking';
import {
    calculateAnnualCashBack,
    calculateAnnualInterest,
    calculateTotalEarnings,
    formatEarnings,
    formatWholeDollarValue,
    roundUpToNext,
} from '../utils/earningsFormulas';

const TOOLTIP_GROUP_NAME = 'mm-earnings-calculator';

interface BaseFrameProps {
    isOpen: boolean;
    onClose: (interactionEventData: EventData) => void;
    onPressPrimaryButton?: (interactionEventData: EventData) => void;
    primaryButtonText?: string;
}

type MoneyMapFrameProps = BaseFrameProps & {
    variant: 'moneyMap';
    moneyMap: MoneyMapWithRulesAndAccounts;
    holisticSummary: HolisticSummary;
    isInGrove?: boolean;
};

type OnboardingFrameProps = BaseFrameProps & {
    variant: 'onboarding';
    monthlyIncome: number;
    monthlyBills: number;
    monthlySpending?: number;
    monthlySavingsContribution?: number;
    startingCashBalance?: number;
};

type EarningsCalculatorFrameProps = MoneyMapFrameProps | OnboardingFrameProps;

const EarningsCalculatorFrame = ({
    isOpen,
    onClose,
    onPressPrimaryButton,
    primaryButtonText,
    ...rest
}: EarningsCalculatorFrameProps) => {
    const [, setIsSliding] = useState(false);
    const isMoneyMapFrame = rest.variant === 'moneyMap';
    const isInGrove = isMoneyMapFrame ? (rest.isInGrove ?? false) : false;

    const calculatorInputs = isMoneyMapFrame
        ? useMoneyMapEarningsInputs({
              moneyMap: rest.moneyMap,
              holisticSummary: rest.holisticSummary,
          })
        : useOnboardingEarningsInputs({
              monthlyIncome: rest.monthlyIncome,
              monthlyBills: rest.monthlyBills,
          });
    const [monthlySpendingValue, setMonthlySpendingValue] = useState(
        calculatorInputs.monthlySpendingDefault,
    );
    const [monthlySavingsValue, setMonthlySavingsValue] = useState(
        calculatorInputs.monthlySavingsDefault,
    );
    const [startingCashValue, setStartingCashValue] = useState(
        calculatorInputs.startingCashDefault,
    );

    const {
        interactionEventData,
        resetInteractionEventData,
        handleTimeFrameChange,
        handleLimitExceeded,
        handleSliderInteraction,
    } = useCountCalculatorInteractions();

    const handleClose = useCallback(() => {
        onClose(interactionEventData);
        resetInteractionEventData();
    }, [onClose, interactionEventData, resetInteractionEventData]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setMonthlySpendingValue(calculatorInputs.monthlySpendingDefault);
        setMonthlySavingsValue(calculatorInputs.monthlySavingsDefault);
        setStartingCashValue(calculatorInputs.startingCashDefault);
    }, [
        isOpen,
        calculatorInputs.monthlySpendingDefault,
        calculatorInputs.monthlySavingsDefault,
        calculatorInputs.startingCashDefault,
    ]);

    const handlePrimaryButtonPress = useCallback(() => {
        onPressPrimaryButton?.(interactionEventData);
        resetInteractionEventData();
    }, [onPressPrimaryButton, interactionEventData, resetInteractionEventData]);

    return (
        <TallSheet isOpen={isOpen} onClose={handleClose} showCloseButton>
            <YStack gap={24} width="100%" marginBottom={16}>
                {isInGrove ? (
                    <YStack gap={8}>
                        <H2Emphasized textAlign="left">
                            Earnings Calculator
                        </H2Emphasized>
                        <BodyMediumRegular textAlign="left" color="$gray1000">
                            Amounts are estimated based on Money Map inputs and
                            configuration in your cash accounts.
                        </BodyMediumRegular>
                    </YStack>
                ) : (
                    <H5Emphasized
                        textAlign="center"
                        alignSelf="center"
                        paddingBottom={isNonMobileWeb ? 16 : 0}
                    >
                        Here&apos;s how much cash you could earn with Fruitful
                    </H5Emphasized>
                )}
                <Calculator
                    monthlyIncome={calculatorInputs.monthlyIncome}
                    monthlySpendingDefault={
                        calculatorInputs.monthlySpendingDefault
                    }
                    monthlySavingsDefault={
                        calculatorInputs.monthlySavingsDefault
                    }
                    startingCashDefault={calculatorInputs.startingCashDefault}
                    totalCash={calculatorInputs.totalCash}
                    monthlySpendingValue={monthlySpendingValue}
                    monthlySavingsValue={monthlySavingsValue}
                    startingCashValue={startingCashValue}
                    onMonthlySpendingChange={setMonthlySpendingValue}
                    onMonthlySavingsChange={setMonthlySavingsValue}
                    onStartingCashChange={setStartingCashValue}
                    onTimeFrameChange={handleTimeFrameChange}
                    onLimitExceeded={handleLimitExceeded}
                    onSliderInteraction={handleSliderInteraction}
                    onSliderStartStop={setIsSliding}
                />
            </YStack>
            {onPressPrimaryButton && primaryButtonText != null && (
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text={primaryButtonText}
                    onPress={handlePrimaryButtonPress}
                />
            )}
            <YStack paddingHorizontal={8}>
                <MoneyMapEarningsCalculatorDisclosures />
            </YStack>
            <TooltipGroupProvider tooltipGroupName={TOOLTIP_GROUP_NAME} />
        </TallSheet>
    );
};

export const MoneyMapEarningsCalculatorFrame = (
    props: Omit<MoneyMapFrameProps, 'variant'>,
) => {
    return <EarningsCalculatorFrame variant="moneyMap" {...props} />;
};

const Calculator = React.memo(
    ({
        monthlyIncome,
        monthlySpendingDefault,
        monthlySavingsDefault,
        startingCashDefault,
        totalCash,
        onTimeFrameChange,
        onLimitExceeded,
        onSliderInteraction,
        onSliderStartStop,
        monthlySpendingValue,
        monthlySavingsValue,
        startingCashValue,
        onMonthlySpendingChange,
        onMonthlySavingsChange,
        onStartingCashChange,
    }: {
        monthlyIncome: number;
        monthlySpendingDefault: number;
        monthlySavingsDefault: number;
        startingCashDefault: number;
        totalCash: number;
        onTimeFrameChange: () => void;
        onLimitExceeded: () => void;
        onSliderInteraction: (slider: SliderType) => void;
        onSliderStartStop?: (isSliding: boolean) => void;
        monthlySpendingValue: number;
        monthlySavingsValue: number;
        startingCashValue: number;
        onMonthlySpendingChange: (value: number) => void;
        onMonthlySavingsChange: (value: number) => void;
        onStartingCashChange: (value: number) => void;
    }) => {
        const annualCashBack = useMemo(
            () => calculateAnnualCashBack(monthlySpendingValue),
            [monthlySpendingValue],
        );
        const annualInterest = useMemo(
            () =>
                calculateAnnualInterest(
                    monthlySpendingValue,
                    monthlySavingsValue,
                    startingCashValue,
                ),
            [monthlySpendingValue, monthlySavingsValue, startingCashValue],
        );
        const isExceedingLimit = useMemo(
            () => monthlySpendingValue + monthlySavingsValue > monthlyIncome,
            [monthlySpendingValue, monthlySavingsValue, monthlyIncome],
        );

        const horizontalPadding = 16;
        const maxMonthlyIncome = Math.max(
            monthlyIncome,
            monthlySpendingDefault,
            1,
        );
        const maxTotalCash = Math.max(totalCash, 1);

        return (
            <YStack
                backgroundColor="$gray200"
                borderRadius={24}
                paddingHorizontal={horizontalPadding}
                paddingVertical={24}
            >
                <YStack height={200}>
                    <CalculatorValuesSection
                        annualCashBack={annualCashBack}
                        annualInterest={annualInterest}
                        onTimeFrameChange={onTimeFrameChange}
                    />
                </YStack>
                <Separator
                    marginHorizontal={-horizontalPadding}
                    borderColor="$gray600"
                    marginTop={24}
                    marginBottom={12}
                />
                <YStack gap={8}>
                    <SliderRow
                        label="Monthly spending"
                        defaultValue={monthlySpendingDefault}
                        maxValue={maxMonthlyIncome}
                        value={monthlySpendingValue}
                        showWarning={isExceedingLimit}
                        tooltipText="How much you plan to spend on your Fruitful Cards each month."
                        updateMode="onSlideMove"
                        onValueChange={onMonthlySpendingChange}
                        onSliderStartStop={onSliderStartStop}
                        onSliderInteraction={() => {
                            onSliderInteraction('spend');
                        }}
                    />
                    <SliderRow
                        label="Monthly savings contribution"
                        defaultValue={monthlySavingsDefault}
                        maxValue={maxMonthlyIncome}
                        value={monthlySavingsValue}
                        showWarning={isExceedingLimit}
                        tooltipText="How much you plan to route towards your savings goals each month."
                        updateMode="onSlideMove"
                        onValueChange={onMonthlySavingsChange}
                        onSliderStartStop={onSliderStartStop}
                        onSliderInteraction={() => {
                            onSliderInteraction('save');
                        }}
                    />
                    <SliderRow
                        label="Starting cash balance"
                        defaultValue={startingCashDefault}
                        maxValue={maxTotalCash}
                        value={startingCashValue}
                        tooltipText="How much you could transfer into your Fruitful accounts."
                        updateMode="onSlideMove"
                        onValueChange={onStartingCashChange}
                        onSliderStartStop={onSliderStartStop}
                        onSliderInteraction={() => {
                            onSliderInteraction('cash');
                        }}
                    />
                </YStack>
                <ExceedingLimitWarning
                    isExceedingLimit={isExceedingLimit}
                    monthlyIncome={monthlyIncome}
                    onLimitExceeded={onLimitExceeded}
                />
            </YStack>
        );
    },
);

const CalculatorValuesSection = ({
    annualCashBack,
    annualInterest,
    onTimeFrameChange,
}: {
    annualCashBack: number;
    annualInterest: number;
    onTimeFrameChange: () => void;
}) => {
    const { getColor } = useThemeColors();
    const [tabViewIndex, setTabViewIndex] = useState(0);
    const routes: Route[] = useMemo(
        () => [
            { key: 'Annual', title: 'Annual' },
            { key: 'Monthly', title: 'Monthly' },
        ],
        [],
    );

    const renderTabBar = useCallback(
        (
            props: SceneRendererProps & {
                navigationState: NavigationState<Route>;
            },
        ) => {
            const itemWidth = 70;
            const barWidth = itemWidth * routes.length;
            const barHeight = 32;
            const borderRadius = 12;
            return (
                <XStack alignItems="center" justifyContent="center">
                    <TabBar
                        {...props}
                        activeColor={getColor('$black')}
                        inactiveColor={getColor('$gray1000')}
                        indicatorStyle={{
                            backgroundColor: getColor('$white'),
                            height: '100%',
                            width: '50%',
                            borderRadius,
                        }}
                        style={{
                            backgroundColor: getColor('$gray400'),
                            width: barWidth,
                            height: barHeight,
                            borderRadius,
                            ...(isWeb && { boxShadow: 'none' }),
                        }}
                        renderTabBarItem={({ route }) => (
                            <XStack
                                height="100%"
                                width={itemWidth}
                                alignItems="center"
                                justifyContent="center"
                                paddingVertical={9}
                                cursor="pointer"
                                onPress={() => {
                                    setTabViewIndex(routes.indexOf(route));
                                    onTimeFrameChange();
                                }}
                            >
                                <LabelRegular>{route.title}</LabelRegular>
                            </XStack>
                        )}
                    />
                </XStack>
            );
        },
        [routes],
    );

    const renderScene = useCallback(() => {
        // This approach for the scenes (versus the more common use of the route param and a switch statement)
        // allows for us to apply custom transition animation/styles in the child component
        return (
            <>
                <CalculatorValuesView
                    isActive={tabViewIndex === 0} // annual
                    cashBack={annualCashBack}
                    interest={annualInterest}
                />
                <CalculatorValuesView
                    isActive={tabViewIndex === 1} // monthly
                    cashBack={annualCashBack / 12} // We're simplifying/approximating monthly as 1/12th of annual value
                    interest={annualInterest / 12}
                />
            </>
        );
    }, [annualCashBack, annualInterest, tabViewIndex]);

    return (
        <TabView
            onIndexChange={setTabViewIndex}
            navigationState={{
                index: tabViewIndex,
                routes,
            }}
            renderTabBar={renderTabBar}
            renderScene={renderScene}
            animationEnabled={false}
        />
    );
};

const CalculatorValuesView = ({
    isActive,
    cashBack,
    interest,
}: {
    isActive: boolean;
    cashBack: number;
    interest: number;
}) => {
    const totalEarnings = calculateTotalEarnings(cashBack, interest);

    // Using ref and effect to ensure we can animate changes from original value (ref) to new value
    const totalEarningsRef = useRef(0);
    useEffect(() => {
        totalEarningsRef.current = totalEarnings;
    }, [totalEarnings]);

    if (!isActive) {
        return null;
    }

    return (
        <YStack
            gap={24}
            paddingTop={24}
            enterStyle={{ opacity: 0.7 }}
            exitStyle={{ opacity: 0.7 }}
            animation="quick"
            animateOnly={['opacity']}
        >
            <XStack justifyContent="center" alignItems="center">
                <H1Emphasized paddingLeft={8} marginRight={isWeb ? -2 : 2}>
                    $
                </H1Emphasized>
                <HeroEmphasized
                    style={
                        isWeb && {
                            fontVariantNumeric: 'tabular-nums', // not available on native
                        }
                    }
                    {...redactPrivate()}
                >
                    <CountUp
                        isCounting={true}
                        key={totalEarnings} // ensures we re-render/re-animate when value changes
                        start={totalEarningsRef.current - 10} // on native, just totalEarningsRef.current doesn't work, so its altered arbitrarily
                        end={totalEarnings}
                        duration={0.2}
                        updateInterval={0.03}
                        formatter={(value: number) =>
                            formatEarnings(value, false)
                        }
                    />
                </HeroEmphasized>
                <XStack marginLeft={8} width={28} height={28}>
                    <Icon
                        icon={FruitfulLogoIcon}
                        color="$green1000"
                        size={28}
                    />
                </XStack>
            </XStack>
            <YStack gap={8} paddingBottom={4}>
                <XStack justifyContent="space-between" alignItems="center">
                    <BodyMediumRegular color="$gray1000">
                        2% cash back¹
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$gray1000" {...redactPrivate()}>
                        {formatEarnings(cashBack)}
                    </BodyMediumRegular>
                </XStack>
                <XStack justifyContent="space-between" alignItems="center">
                    <BodyMediumRegular color="$gray1000">
                        {currentApy()} Annual Percentage Yield²
                    </BodyMediumRegular>
                    <BodyMediumRegular color="$gray1000" {...redactPrivate()}>
                        {formatEarnings(interest)}
                    </BodyMediumRegular>
                </XStack>
            </YStack>
        </YStack>
    );
};

const SliderRow = React.memo(
    ({
        label,
        defaultValue,
        maxValue,
        value,
        tooltipText,
        updateMode,
        onValueChange,
        onSliderInteraction,
        onSliderStartStop,
        showWarning,
    }: {
        label: string;
        defaultValue: number;
        maxValue: number;
        value: number;
        tooltipText: string;
        updateMode: 'onSlideMove' | 'onSlidePause' | 'onSlideEnd';
        onValueChange: (value: number) => void;
        onSliderInteraction: () => void;
        onSliderStartStop?: (isSliding: boolean) => void;
        showWarning?: boolean;
    }) => {
        // This component works with two levels of "value":
        // - "inner" value is what the true value of the slider input and label, which is updated on every movement
        // - "outer" value is the value provided by the parent and published for the parent, which is handled based on updateMode
        // The debounce callback below allows us to publish updates to the value when the sliding pauses, where otherwise we could
        // only update the out value on every movement (onSlideMove) or when the slider is released (onSlideEnd).
        const [innerValue, setInnerValue] = useState(value);
        const debouncedSlideHandler = useDebouncedCallback(
            (outerValue: number) => {
                onValueChange(outerValue);
                onSliderStartStop?.(false);
            },
            80,
        );

        useEffect(() => {
            setInnerValue(value);
        }, [value]);

        return (
            <YStack
                gap={4}
                paddingTop={6}
                paddingBottom={10}
                paddingHorizontal={8}
            >
                <XStack justifyContent="space-between" alignItems="center">
                    <XStack alignItems="center">
                        <BodyMediumRegular marginRight={-4}>
                            {label}
                        </BodyMediumRegular>
                        <InfoButtonWithTooltip
                            tooltipContent={tooltipText}
                            tooltipGroupName={TOOLTIP_GROUP_NAME}
                            subtle
                        />
                    </XStack>
                    <BodyMediumRegular {...redactPrivate()}>
                        {formatWholeDollarValue(innerValue)}
                    </BodyMediumRegular>
                </XStack>
                <Slider
                    width="100%"
                    paddingHorizontal={1}
                    value={[innerValue]}
                    defaultValue={[defaultValue]}
                    max={maxValue}
                    min={0}
                    step={10}
                    tone={showWarning ? 'warning' : 'positive'}
                    onSlideMove={(_, value: number) => {
                        const newValue = roundUpToNext(value, 10); // 'step' not working as expected
                        setInnerValue(newValue);
                        onSliderStartStop?.(true);

                        if (updateMode === 'onSlideMove') {
                            onValueChange(newValue);
                        } else if (updateMode === 'onSlidePause') {
                            debouncedSlideHandler(newValue);
                        }
                    }}
                    onSlideEnd={() => {
                        onSliderInteraction();
                        onSliderStartStop?.(false);
                        if (updateMode === 'onSlideEnd') {
                            onValueChange(innerValue);
                        }
                    }}
                />
            </YStack>
        );
    },
);

const ExceedingLimitWarning = ({
    isExceedingLimit,
    monthlyIncome,
    onLimitExceeded,
}: {
    isExceedingLimit: boolean;
    monthlyIncome: number;
    onLimitExceeded: () => void;
}) => {
    const prevIsExceedingLimit = useRef(isExceedingLimit);
    useEffect(() => {
        if (!prevIsExceedingLimit.current && isExceedingLimit) {
            onLimitExceeded();
        }
        prevIsExceedingLimit.current = isExceedingLimit;
    }, [isExceedingLimit, onLimitExceeded]);

    return (
        // Accordion used below for animated transition with showing/hiding
        <Accordion type="single" value={isExceedingLimit.toString()}>
            <Accordion.Item value="true" asChild>
                <Accordion.HeightAnimator
                    animation="slideScreen"
                    animateOnly={['height', 'opacity', 'transform']}
                >
                    <Accordion.Content
                        backgroundColor="transparent"
                        padding={0}
                    >
                        <XStack
                            alignItems="center"
                            marginTop={16}
                            marginHorizontal={2}
                            padding={16}
                            gap={12}
                            backgroundColor="$gray300"
                            borderRadius={16}
                            alignSelf="center"
                            enterStyle={{
                                opacity: 0,
                                scale: 0.98,
                            }}
                            exitStyle={{
                                opacity: 0,
                                scale: 0.98,
                            }}
                            animation="slideScreen"
                            animateOnly={['height', 'opacity', 'transform']}
                            $sm={{ maxWidth: 380 }}
                        >
                            <XStack width={20}>
                                <Icon
                                    icon={CircleAlertIcon}
                                    color="$black"
                                    size={20}
                                />
                            </XStack>
                            <FootnoteRegular {...redactPrivate()} flex={1}>
                                You&apos;ve exceeded your{' '}
                                {formatWholeDollarValue(monthlyIncome)} monthly
                                income limit for spending and savings
                            </FootnoteRegular>
                        </XStack>
                    </Accordion.Content>
                </Accordion.HeightAnimator>
            </Accordion.Item>
        </Accordion>
    );
};

const MoneyMapEarningsCalculatorDisclosures = React.memo(() => {
    return (
        <YStack
            gap={12}
            paddingBottom={isNonMobileWeb ? 0 : 48}
            paddingTop={16}
            paddingHorizontal={2}
        >
            <LabelRegular color="$gray900">
                This calculator is for educational purposes only and provides
                estimates based on the information you enter. Results are not
                guaranteed and do not determine eligibility or terms for any
                specific product or service.
            </LabelRegular>
            <LabelRegular color="$gray900">
                Fruitful cannot and does not guarantee the applicability or
                accuracy of the calculations in regards to your individual
                circumstances. The examples shown are hypothetical and meant to
                illustrate the impact of compounding interest and potential
                earnings. They do not reflect any specific investment or
                security, and actual returns and principal values may vary.
            </LabelRegular>
            <BankingGeneralDisclosure color="$gray900" />
            <CashBackDisclosure superscript={1} color="$gray900" />
            <CashApyDisclosure superscript={2} color="$gray900" />
            <InvestmentManagementDisclosure color="$gray900" />
        </YStack>
    );
});
